import React, { useMemo } from 'react';
import useMobileMediaQuery from '../../hooks/useMobileMediaQuery';
import CustomTooltip from '../Tooltip/Tooltip';
import LetterAvatar from './LetterAvatar';
import './LetterAvatarGroup.scss';

export interface LetterAvatarGroupProps {
  userRoles: string[];
  /** Optional display names keyed by email, used for first + last initials. */
  displayNames?: Record<string, string | undefined>;
}

function LetterAvatarGroup({ userRoles, displayNames }: LetterAvatarGroupProps) {
  const isMobile = useMobileMediaQuery();
  const isTripsList = window.location.pathname === '/trips';
  const visibleLetterAvatars = useMemo(
    () => (isMobile && isTripsList ? 4 : 5),
    [isMobile, isTripsList]
  );
  const remainingRolesCount = useMemo(
    () => Math.max(0, (userRoles?.length ?? 0) - visibleLetterAvatars),
    [userRoles?.length, visibleLetterAvatars]
  );

  const visibleEmails = useMemo(
    () => userRoles?.slice(0, visibleLetterAvatars) ?? [],
    [userRoles, visibleLetterAvatars]
  );
  const hiddenEmails = useMemo(
    () => userRoles?.slice(visibleLetterAvatars) ?? [],
    [userRoles, visibleLetterAvatars]
  );

  if (!userRoles?.length || userRoles.length <= 1) {
    return null;
  }

  const itemClass = `letter-avatar-item${isMobile ? ' mobile' : ''}`;

  return (
    <div className='letter-avatar-group-container'>
      {visibleEmails.map((email, index) => (
        <div
          key={email}
          className={itemClass}
          style={{ zIndex: index }}
        >
          <CustomTooltip title={email}>
            <div>
              <LetterAvatar
                string={email}
                displayName={displayNames?.[email]}
                capitalize
              />
            </div>
          </CustomTooltip>
        </div>
      ))}
      {remainingRolesCount > 0 && (
        <div
          className={itemClass}
          style={{ zIndex: visibleLetterAvatars }}
        >
          <CustomTooltip
            title={
              <div>
                {hiddenEmails.map((email) => (
                  <div
                    key={email}
                    className='tooltip-item'
                  >
                    {email}
                  </div>
                ))}
              </div>
            }
          >
            <div>
              <LetterAvatar
                string=''
                remainingRolesCount={remainingRolesCount}
              />
            </div>
          </CustomTooltip>
        </div>
      )}
    </div>
  );
}

LetterAvatarGroup.displayName = 'LetterAvatarGroup';
export default LetterAvatarGroup;
